import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { ICON_NAMES, getIcon } from "@/lib/icons";
import type { NewService, Service } from "@shared/schema";

export function AdminServicesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Service | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api<Service[]>("/api/admin/services"),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/services/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      qc.invalidateQueries({ queryKey: ["services"] });
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Szolgáltatások</h1>
        <button
          type="button"
          onClick={() => {
            setCreating(true);
            setEditing(null);
          }}
          className="btn-primary"
        >
          <Plus size={16} /> Új szolgáltatás
        </button>
      </div>

      <p className="mt-2 text-sm text-brand-500">
        Ezek a kártyák jelennek meg a kezdőoldal "Szolgáltatásaink" rácsában.
      </p>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="card overflow-hidden">
          {q.isLoading && (
            <div className="p-6 text-brand-500">Betöltés...</div>
          )}
          {q.data?.length === 0 && (
            <div className="p-6 text-brand-500">Még nincs szolgáltatás.</div>
          )}
          <ul className="divide-y divide-brand-100">
            {q.data?.map((s) => {
              const Icon = getIcon(s.icon);
              return (
                <li
                  key={s.id}
                  className={`p-4 flex items-center gap-4 ${
                    editing?.id === s.id ? "bg-brand-50" : ""
                  }`}
                >
                  <div className="w-10 h-10 bg-brand-100 text-accent grid place-items-center shrink-0">
                    <Icon size={18} />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(s);
                      setCreating(false);
                    }}
                    className="flex-1 text-left"
                  >
                    <div className="font-medium text-sm">
                      {s.title}{" "}
                      {!s.isActive && (
                        <span className="ml-2 text-xs bg-brand-100 text-brand-500 px-1.5 py-0.5 rounded">
                          inaktív
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <div className="text-xs text-brand-500 mt-0.5 line-clamp-1">
                        {s.description}
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Törli: ${s.title}?`)) del.mutate(s.id);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          {(editing || creating) && (
            <ServiceEditor
              key={editing?.id ?? "new"}
              service={editing}
              onSaved={() => {
                setEditing(null);
                setCreating(false);
                qc.invalidateQueries({ queryKey: ["admin", "services"] });
                qc.invalidateQueries({ queryKey: ["services"] });
              }}
              onCancel={() => {
                setEditing(null);
                setCreating(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceEditor({
  service,
  onSaved,
  onCancel,
}: {
  service: Service | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const isNew = !service;
  const [draft, setDraft] = useState<Partial<NewService>>({
    title: service?.title ?? "",
    description: service?.description ?? "",
    icon: service?.icon ?? "Building2",
    sortOrder: service?.sortOrder ?? 0,
    isActive: service?.isActive ?? true,
  });

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api("/api/admin/services", { method: "POST", body: draft })
        : api(`/api/admin/services/${service!.id}`, {
            method: "PATCH",
            body: draft,
          }),
    onSuccess: onSaved,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="card p-6 space-y-4"
    >
      <h3 className="font-semibold">
        {isNew ? "Új szolgáltatás" : draft.title || "Szolgáltatás szerkesztése"}
      </h3>

      <div>
        <label className="label">Cím *</label>
        <input
          required
          className="input"
          value={draft.title ?? ""}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Leírás</label>
        <textarea
          rows={3}
          className="input"
          value={draft.description ?? ""}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Ikon</label>
        <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-brand-200 rounded-md bg-brand-50">
          {ICON_NAMES.map((name) => {
            const Icon = getIcon(name);
            const selected = draft.icon === name;
            return (
              <button
                type="button"
                key={name}
                onClick={() => setDraft({ ...draft, icon: name })}
                title={name}
                className={`p-2 rounded ${
                  selected
                    ? "bg-accent text-white"
                    : "bg-white text-brand-600 hover:bg-brand-100"
                }`}
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-brand-500">
          Kiválasztva: <code>{draft.icon}</code>
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Sorrend</label>
          <input
            type="number"
            className="input"
            value={draft.sortOrder ?? 0}
            onChange={(e) =>
              setDraft({ ...draft, sortOrder: Number(e.target.value) })
            }
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm pb-3">
            <input
              type="checkbox"
              checked={draft.isActive ?? true}
              onChange={(e) =>
                setDraft({ ...draft, isActive: e.target.checked })
              }
            />
            Aktív
          </label>
        </div>
      </div>

      {save.isError && (
        <div className="text-sm text-red-600">
          {save.error instanceof ApiError ? save.error.message : "Mentési hiba"}
        </div>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={save.isPending} className="btn-primary">
          {save.isPending ? "Mentés..." : "Mentés"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Mégse
        </button>
      </div>
    </form>
  );
}
